import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import StartSettings from "../components/startSettings";
import { getCategoryBySlug, getParentSlugOfId, shareUrl } from "../helper/Data";
import pageTransition from "../components/pageTransition";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import MetaHelmet from "../components/metaHelmet";
import ArticleStructuredData from "../components/articleStructuredData";
import BreadcrumbStructuredData from "../components/breadcrumbStructuredData";
import LoadingScreen from "../components/loadingScreen";
import VenueCard from "../components/venueCard";
import { getVenueOpeningHours, openValueToTimeString } from "../helper/Filter";
import { isHoliday } from "../helper/Holiday";

interface PostProps {
    newsLoaded: boolean;
}

// Interface für den Post
interface Post {
    id: number;
    slug: string;
    title: string;
    content: string;
    featured_media: string;
    thumbnail: string;
    publish_date: string;
    links: {
        categories: Array<CategoryLink>;  // Define the type of category links
        venues: Array<VenueLink>;         // Define the type of venue links
    };
}

interface CategoryLink {
    'post_link-category': string;
}

interface VenueLink {
    'post_link-venue': {
        title: string;
    };
}

interface Venue {
    id: number;
    title: string;
    slug: string;
    partner_status: string;
    thumbnail?: string;
    restaurant__opening_hours: {
        "restaurant__opening_hours-open": string;
        "restaurant__opening_hours-close": string;
        "restaurant__opening_hours-dow_till": string;
        "restaurant__opening_hours-warm_cuisine": boolean;
        "restaurant__opening_hours-dow_from": string;
    }[];
}

interface Category {
    id: number;
    parent_id: number;
    slug: string;
    name_de: string;
    description_de: string;
    name_en: string;
    description_en: string;
    search_str: string;
}

const Post: React.FC<PostProps> = ({ newsLoaded }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Use `useLocation` to extract venue
    const location = useLocation();
    const internalOrigin = location.state?.fromInternal || false;
    const path = location.pathname.substring(1);
    const postSlug = path.split('/')[1];

    const { trackPageView } = useMatomo();

    // Load settings
    const [settingsSet] = useState<boolean>(() => !!localStorage.getItem('settings-set'));
    const [language] = useState(localStorage.getItem('i18nextLng') || 'en');

    const [post, setPost] = useState<Post | null>(null); // Verwende das Post-Interface

    function getSinglePost(posts: Post[], slug: string): Post | undefined {
        let result = posts.filter((item: Post) => item.slug === slug);
        return result[0];
    }

    const getSlugOfCategory = (categoryUrl: any) => {
        const url = String(categoryUrl['post_link-category']);

        const slug = url.split('/').filter(Boolean).pop();

        if(typeof slug === 'string') {
            return slug;
        }

        return "";
    }

    const getSlugOfVenue = (categoryUrl: any) => {
        const url = String(categoryUrl['post_link-venue']['url']);
        if(url === undefined) return "";

        const slug = url.split('/').filter(Boolean).pop();

        if(typeof slug === 'string') {
            return slug;
        }

        return "";
    }

    function getVenueBySlug(slug: string, venues: Venue[]): Venue | null {
        const foundVenue = venues.find((venue) => venue.slug === slug);
        return foundVenue || null;
    }

    function getCategoryBySlugFromList(slug: string, categories: Category[]): Category | null {
        const foundCategory = categories.find((category) => category.slug === slug);
        return foundCategory || null;
    }

    function getOpenStatus(openingHours: Venue["restaurant__opening_hours"]): { status: string; isOpen: boolean } {
        if (!openingHours) {
            return { status: t('no_regular_hours'), isOpen: false };
        }

        const currentDate = new Date();
        const hour = currentDate.getHours();
        const minutes = currentDate.getMinutes() / 60;
        let dow = currentDate.getDay();
        if (dow === 0) {
            dow = 7;
        }
        // Feiertagcheck
        const holidayCheck = isHoliday(currentDate);
        if (holidayCheck.isHoliday) {
            dow = 8;
        }

        const time = hour + minutes;
        const openValue = getVenueOpeningHours(openingHours, time, dow);

        if (openValue > 0) {
            return { status: t('todayUntil') + openValueToTimeString(openValue), isOpen: true };
        } else if (openValue < 0) {
            return { status: t('closed') + t('opensAt') + openValueToTimeString(-openValue), isOpen: false };
        } else {
            return { status: t('closed'), isOpen: false };
        }
    }

    // Parse content and extract venue and category slugs for replacement
    const parseContentWithVenuesAndCategories = useMemo(() => {
        if (!post?.content) return { parts: [], venueData: [], categoryData: [] };

        const tempVenues = localStorage.getItem('venues');
        const venues: Venue[] = tempVenues ? JSON.parse(tempVenues) : [];

        const tempCategories = localStorage.getItem('categories');
        const categories: Category[] = tempCategories ? JSON.parse(tempCategories) : [];

        // Combined regex for both venue and category patterns
        const venueRegex = /<p class="venue"><a href="https?:\/\/[^/]+\/betriebe\/([^"]+)">[^<]*<\/a><\/p>/g;
        const categoryRegex = /<p class="category"><a href="https?:\/\/[^/]+\/[^/]+\/([^"]+)">[^<]*<\/a><\/p>/g;

        // First pass: find all matches and their positions
        interface MatchInfo {
            index: number;
            length: number;
            type: 'venue' | 'category';
            slug: string;
        }
        const allMatches: MatchInfo[] = [];

        let match;
        while ((match = venueRegex.exec(post.content)) !== null) {
            allMatches.push({
                index: match.index,
                length: match[0].length,
                type: 'venue',
                slug: match[1]
            });
        }

        while ((match = categoryRegex.exec(post.content)) !== null) {
            allMatches.push({
                index: match.index,
                length: match[0].length,
                type: 'category',
                slug: match[1]
            });
        }

        // Sort by position
        allMatches.sort((a, b) => a.index - b.index);

        const parts: string[] = [];
        const venueData: { slug: string; venue: Venue | null; status: string; isOpen: boolean }[] = [];
        const categoryData: { slug: string; category: Category | null }[] = [];

        let lastIndex = 0;

        for (const matchInfo of allMatches) {
            // Add content before the match
            if (matchInfo.index > lastIndex) {
                parts.push(post.content.substring(lastIndex, matchInfo.index));
            }

            if (matchInfo.type === 'venue') {
                const venue = getVenueBySlug(matchInfo.slug, venues);
                const openStatus = venue ? getOpenStatus(venue.restaurant__opening_hours) : { status: '', isOpen: false };
                parts.push(`__VENUE_CARD_${venueData.length}__`);
                venueData.push({ slug: matchInfo.slug, venue, status: openStatus.status, isOpen: openStatus.isOpen });
            } else {
                const category = getCategoryBySlugFromList(matchInfo.slug, categories);
                parts.push(`__CATEGORY_LINK_${categoryData.length}__`);
                categoryData.push({ slug: matchInfo.slug, category });
            }

            lastIndex = matchInfo.index + matchInfo.length;
        }

        // Add remaining content
        if (lastIndex < post.content.length) {
            parts.push(post.content.substring(lastIndex));
        }

        return { parts, venueData, categoryData };
    }, [post?.content]);

    useEffect(() => {
        if (!newsLoaded) return;
        trackPageView({
            href: window.location.href, // URL der aktuellen Seite
          });

        let tempPosts = localStorage.getItem('news');
        if (tempPosts) {
            const parsedPosts: Post[] = JSON.parse(tempPosts);
            const postItem = getSinglePost(parsedPosts, postSlug) || null;

            if (postItem) {
                setPost(postItem);
            }
            else {
                navigate('/notfound');
            }  
        }
    }, [location, newsLoaded]);
    
    if (!settingsSet) {
        return (
            <ion-page class="sg-body">
                <StartSettings />
            </ion-page>
        );
    }

    if (!newsLoaded) {
        return (
            <ion-page class="sg-body">
                <LoadingScreen />
            </ion-page>
        );
    }

    return (
        <ion-page class="sg-body post-page">
            <div className="page-container news">
                <MetaHelmet 
                    title={post?.title ? (post.title.toLowerCase().includes('graz') ? post.title : `${post.title} - Graz`) : ''}
                    desc={post?.title ? (post.title.toLowerCase().includes('graz') ? `Alle Details zu ${post.title} findest du auf smartGastro.` : `Alle Details zu ${post.title} in Graz findest du auf smartGastro.`) : ''}
                    og_type="article"
                />
                {post && (
                    <>
                        <ArticleStructuredData
                            title={post.title.toLowerCase().includes('graz') ? post.title : `${post.title} - Graz`}
                            description={post.title.toLowerCase().includes('graz') ? `Alle Details zu ${post.title} findest du auf smartGastro.` : `Alle Details zu ${post.title} in Graz findest du auf smartGastro.`}
                            imageUrl={post.featured_media}
                            datePublished={post.publish_date}
                        />
                        <BreadcrumbStructuredData
                            items={[
                                { name: 'Start', url: 'https://smartgastro.at/' },
                                { name: 'Aktuelles', url: 'https://smartgastro.at/aktuelles' },
                                { name: post.title, url: `https://smartgastro.at/aktuelles/${post.slug}` },
                            ]}
                        />
                    </>
                )}
                <div>
                    <img className="post-image" src={post?.featured_media} alt={post?.title || ''} />
                    <span className="post-date ion-padding">{post?.publish_date}</span>
                    <div className="header-container ion-padding news">
                        {internalOrigin && <button className="arrow-back" style={{ backgroundImage: `url("/icons/back_arrow_white.svg")` }} onClick={() => navigate(-1)}></button>}
                        <h1 className="start-title">{post?.title}</h1>
                        <span onClick={() => shareUrl()} className="share-icon"></span>
                    </div>
                    <div className="ion-padding news-container">
                        <div className="news-content">
                            {parseContentWithVenuesAndCategories.parts.map((part, index) => {
                                const venueMatch = part.match(/^__VENUE_CARD_(\d+)__$/);
                                if (venueMatch) {
                                    const venueIndex = parseInt(venueMatch[1], 10);
                                    const data = parseContentWithVenuesAndCategories.venueData[venueIndex];
                                    if (data && data.venue) {
                                        return (
                                            <VenueCard
                                                key={`venue-${index}`}
                                                name={data.venue.title}
                                                slug={data.slug}
                                                status={data.status}
                                                cssClass={data.isOpen ? 'open' : 'closed'}
                                                additionalInformation=""
                                                eventLink=""
                                                menuLink=""
                                                isPartner={false}
                                                thumbnail={data.venue.thumbnail}
                                            />
                                        );
                                    }
                                    return null;
                                }
                                const categoryMatch = part.match(/^__CATEGORY_LINK_(\d+)__$/);
                                if (categoryMatch) {
                                    const categoryIndex = parseInt(categoryMatch[1], 10);
                                    const data = parseContentWithVenuesAndCategories.categoryData[categoryIndex];
                                    if (data && data.category) {
                                        return (
                                            <Link
                                                key={`category-${index}`}
                                                state={{ fromInternal: true }}
                                                className="search-results-item"
                                                to={`/${getParentSlugOfId(data.category.parent_id)}/${data.category.slug}`}
                                            >
                                                <span
                                                    className="search-results-item-category"
                                                    style={{ backgroundImage: `url("/icons/${data.category.slug}.svg")` }}
                                                >
                                                    {language === 'de' ? data.category.name_de : data.category.name_en}
                                                </span>
                                            </Link>
                                        );
                                    }
                                    return null;
                                }
                                return <span key={index} dangerouslySetInnerHTML={{ __html: part }} />;
                            })}
                        </div>
                        <div className="news-links">
                            {post?.links?.categories?.map((link: any, index: number) => (
                                <React.Fragment key={index}>
                                    {index === 0 && link['post_link-category'] && <h2 className="search-results-heading post">{t('categories')}</h2>}
                                    {link['post_link-category'] && (
                                        <Link state={{ fromInternal: true }} className="search-results-item" to={link['post_link-category']}>
                                            <span className="search-results-item search-results-item-category news-link" style={{ backgroundImage: `url("/icons/${getSlugOfCategory(link)}.svg")` }}>
                                                {language === 'de' ? getCategoryBySlug(getSlugOfCategory(link))?.name_de : getCategoryBySlug(getSlugOfCategory(link))?.name_en}
                                            </span>
                                        </Link>
                                    )}
                                </React.Fragment>
                            ))}
                            {post?.links?.venues?.map((link: any, index: number) => (
                                <React.Fragment key={index}>
                                    {index === 0 && link['post_link-venue'] && <h2 className="search-results-heading post">{t('venues')}</h2>}
                                    {link['post_link-venue'] && (
                                        <Link state={{ fromInternal: true }} className="search-results-item" to={`/betriebe/${getSlugOfVenue(link)}`}>
                                            {link['post_link-venue']['title']}
                                        </Link>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </ion-page>
    );
}

export default pageTransition(Post);
